"use client";

import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import NISHTHA_QR from "../../images/NISHTHA_QR.png";

const PaymentResult = ({ response, retry }) => {
  const isSuccess = response?.status === "success";
  
  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
        isSuccess ? 'bg-green-100' : 'bg-red-100'
      }`}>
        {isSuccess ? (
          <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
      </div>
      <h2 className={`text-2xl font-bold mb-2 ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
        {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {isSuccess 
          ? 'Your payment has been processed successfully.' 
          : 'There was an issue processing your payment. Please try again.'}
      </p>
      {response?.txnid && (
        <p className="text-sm text-gray-500 mb-4">Transaction ID: {response.txnid}</p>
      )}
      <button
        onClick={retry}
        className="bg-[#4FA0FF] hover:bg-[#3A8CE6] text-white font-semibold py-3 px-8 rounded-lg"
      >
        {isSuccess ? 'Make Another Payment' : 'Try Again'}
      </button>
    </div>
  );
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split("-");
    const isoDateStr = `${yyyy}-${mm}-${dd}`;
    const date = new Date(isoDateStr);
    return isNaN(date) ? null : date;
  }
  const date = new Date(dateStr);
  return isNaN(date) ? null : date;
};

export default function PanVerification({ loading, fetchData }) {
  const [pancard, setPancard] = useState("");
  const [upiSource, setUpiSource] = useState("crm");
  const [paymentResponse, setPaymentResponse] = useState(null);

  const [userData, setUserData] = useState({});
  const [isFetching, setIsFetching] = useState(false);

  // Loans disbursed by Kubermarg keep the legacy loan number format; loans
  // disbursed by Nishtha Leasing use loan numbers prefixed "KMNL" and are
  // repaid to Nishtha's own UPI ID (decoded from NISHTHA_QR.png).
  const isNishtha =
    typeof userData?.loan_no === "string" &&
    userData.loan_no.toUpperCase().startsWith("KMNL");
  const upiId = "nishtha1234qr@fbl";
  const companyName = isNishtha ? "Nishtha Leasing Pvt Ltd" : "";
  const upiPayeeName = isNishtha ? "NISHTHA LEASING PRIVATE LIMITED" : "KMUser";

  const upiLink = `upi://pay?pa=${upiId}&pn=${upiPayeeName}&am=${userData?.["total_due_amount"]}&cu=INR`;

  const handlePAN = (e) => {
    const value = e.target.value.toUpperCase();
    if (
      /^[A-Z]{0,5}$/.test(value.slice(0, 5)) &&
      /^[\d]{0,4}$/.test(value.slice(5, 9)) &&
      /^[A-Z]{0,1}$/.test(value.slice(9))
    ) {
      setPancard(value);
    }
  };

  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return "Invalid Date";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (Object.keys(userData).length > 0) {
      window.location.href = upiLink;
      return;
    }

    setIsFetching(true);

    const fetchRepayment = async (url) => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pancard }),
      });
      if (!res.ok) throw new Error("Failed to fetch repayment details");
      return await res.json();
    };

    try {
      let responseData = await fetchRepayment(
        "https://crm.kubermax.in/p/api/repayment"
      );

      if (responseData.status !== 1 || !responseData.data) {
        responseData = await fetchRepayment(
          "https://crm.kubermax.in/p/api/repayment"
        );
        if (responseData.status === 1 && responseData.data) {
          setUpiSource("crm");
        }
      } else {
        setUpiSource("cms");
      }

      if (responseData.status !== 1 || !responseData.data) {
        setUserData({ message: "PAN not found" });
      } else {
        setUserData(responseData.data);
      }

      console.log("response Data=>", responseData);
    } catch (error) {
      console.error("Error fetching repayment details:", error);
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="flex flex-col px-4 py-4 sm:px-1 sm:py-1" style={{ background: "#FFFFFF" }}>
      {!paymentResponse ? (
        <div className="flex flex-col md:flex-row mt-4 sm:mt-2 border border-[#E5E5E5] rounded-md" style={{ background: "#FFFFFF" }}>
          <div className="w-full md:w-1/2 px-4 py-4 sm:px-2 sm:py-2 border-b md:border-b-0 md:border-r border-[#E5E5E5]" style={{ background: "#FFFFFF" }}>
            <p className="text-[#1D3557] italic mb-5">
              Please verify the accuracy of the below details before doing any
              transfer.
            </p>
            <span className="mb-2 text-[#1D3557]">Please enter your PAN Details</span>
            <input
              type="text"
              value={pancard}
              onChange={handlePAN}
              required
              maxLength={10}
              className="p-3 border border-[#E5E5E5] rounded-md text-base w-full max-w-md text-[#1D3557]"
              style={{ background: "#FFFFFF" }}
            />
            <button
              onClick={handleSubmit}
              className="bg-[#FFD700] mt-2 hover:bg-[#FFE55C] text-[#1D3557] border-none rounded-md py-3 px-8 text-base cursor-pointer transition-colors inline-block font-semibold"
            >
              {isFetching ? (
                <div className="inline-block w-5 h-5 border-4 border-[#1D3557] border-t-transparent rounded-full animate-spin"></div>
              ) : userData?.total_due_amount ? (
              `Pay via UPI ₹${userData.total_due_amount}`
              ) : (
                "View My Loan"
              )}
            </button>
            {Object.keys(userData).length > 0 && (
              <div className="mt-4 sm:mt-6 p-4 bg-gray-50 border border-[#E5E5E5] rounded-lg shadow-md w-full" style={{ background: "#FFFFFF" }}>
                {userData.message ? (
                  <p className="text-center text-red-600 font-semibold">
                    {userData.message}
                  </p>
                ) : (
                  <>
                    {userData.loan_no && userData.total_due_amount && (
                      <div className="mb-4 text-center">
                        <p className="text-lg font-semibold text-[#1D3557]">
                          Total Due Amount: ₹{userData.total_due_amount}
                        </p>
                        {userData.repayment_date && (
                          <p
                            className={`text-sm font-medium mt-1 ${
                              new Date() >= new Date(userData.repayment_date)
                                ? "text-red-600"
                                : "text-[#1D3557]"
                            }`}
                          >
                            Repayment Due Date:{" "}
                            {formatDate(userData.repayment_date)}
                          </p>
                        )}
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-center mb-4 text-[#4FA0FF]">
                      Summary
                    </h3>
                    <div className="space-y-2 text-sm sm:text-base">
                      {userData.loan_no && (
                        <p className="text-[#1D3557]">
                          <strong>Loan No:</strong> {userData.loan_no}
                        </p>
                      )}
                      {userData.repayment_amount && (
                        <p className="text-[#1D3557]">
                          <strong>Repayment Amount:</strong> ₹
                          {userData.repayment_amount}
                        </p>
                      )}
                      {userData.total_interest_amount && (
                        <p className="text-[#1D3557]">
                          <strong>Interest:</strong> ₹
                          {userData.total_interest_amount}
                        </p>
                      )}

                      {userData.disbursal_date && (
                        <p className="text-[#1D3557]">
                          <strong>Disbursal Date:</strong>{" "}
                          {formatDate(userData.disbursal_date)}
                        </p>
                      )}
                      {userData.repayment_date && (
                        <p className="text-[#1D3557]">
                          <strong>Repayment Date:</strong>{" "}
                          {formatDate(userData.repayment_date)}
                        </p>
                      )}
                      {userData.tenure && (
                        <p className="text-[#1D3557]">
                          <strong>Tenure (Days):</strong> {userData.tenure}
                        </p>
                      )}
                      {userData.penalty_days && (
                        <p className="text-[#1D3557]">
                          <strong>Penalty Days</strong> {userData.penalty_days}
                        </p>
                      )}
                      <p className="font-semibold text-[#4FA0FF]">
                        <strong>Total Due Amount:</strong> ₹
                        {userData.total_due_amount}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="w-full md:w-1/2 px-4 py-4 sm:px-2 sm:py-2 flex flex-col items-center justify-center" style={{ background: "#FFFFFF" }}>
            <h3 className="text-xl font-bold mb-2 text-center text-[#1D3557]">
              {companyName}
            </h3>
            <h4 className="text-lg font-semibold mb-4 text-[#1D3557]">Scan to Pay</h4>
            <div className="border border-[#E5E5E5] rounded-md p-3 sm:p-2 mb-2" style={{ background: "#FFFFFF" }}>
              {isNishtha ? (
                <img src={NISHTHA_QR} alt="Nishtha Leasing UPI QR" width={200} height={200} />
              ) : (
                <QRCodeCanvas
                  value={`upi://pay?pa=${upiId}&pn=${userData?.loan_no || upiPayeeName}&am=${userData?.["total_due_amount"]}&cu=INR`}
                  size={200}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 mt-4 w-full max-w-xs">
              <button
                onClick={() =>
                  (window.location.href = `upi://pay?pa=${upiId}&pn=${upiPayeeName}&am=${userData?.["total_due_amount"]}&cu=INR`)
                }
                className="bg-[#FFD700] hover:bg-[#FFE55C] cursor-pointer text-[#1D3557]   py-2 px-4 rounded text-center font-semibold"
              >
                Pay ₹{userData?.["total_due_amount"] || ""} to Server 1
              </button>
              {/* <button
                onClick={() =>
                  (window.location.href = `upi://pay?pa=${upiId}&pn=POSUser&am=${userData?.["total_due_amount"]}&cu=INR`)
                }
                className="bg-[#4FA0FF] hover:bg-[#3A8CE6] cursor-pointer text-white py-2 px-4 rounded text-center font-semibold"
              >
                Pay ₹{userData?.["total_due_amount"] || ""} to Server 2
              </button> */}
              {/* {userData.loan_no && userData.total_due_amount && (
                <>
                  <button
                    disabled={paymentPending}
                    onClick={() => generatePaymentToken(userData.lead_id)}
                    className="bg-[#FFD700] hover:bg-[#FFE55C] text-[#1D3557] font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {!paymentPending
                      ? "Pay Securely via Easebuzz"
                      : "Initiating Payment..."}
                  </button>
                  <button
                    onClick={handlePartPayment}
                    className="bg-[#4FA0FF] hover:bg-[#3A8CE6] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Part Payment
                  </button>
                </>
              )} */}
            </div>
          </div>
        </div>
      ) : (
        <PaymentResult
          retry={() => setPaymentResponse(null)}
          response={paymentResponse}
        />
      )}
      <div className="mt-8"></div>
    </div>
  );
}